import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/** Section with the "Show more" filter values left out of the page HTML (sections/facet-list-values.liquid). */
const FACET_VALUES_SECTION_ID = 'facet-list-values';

/** Placeholder the section leaves in input ids for each list's own "<filter_style>-<in_drawer>" suffix. */
const FACET_ID_SUFFIX_TOKEN = '__FACET_ID_SUFFIX__';

/**
 * One request per page URL (filters/sort included), shared by every lazy list on the page -
 * sidebar and drawer copies of all filters. A failed request is dropped so it can be retried.
 * @type {Map<string, Promise<Document>>}
 */
const facetValuesRequests = new Map();

/**
 * @returns {Promise<Document>}
 */
function loadFacetValues() {
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('section_id', FACET_VALUES_SECTION_ID);
  const key = url.toString();

  let request = facetValuesRequests.get(key);
  if (!request) {
    request = fetch(key)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load filter values: ${response.status}`);
        return response.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
    request.catch(() => facetValuesRequests.delete(key));
    facetValuesRequests.set(key, request);
  }

  return request;
}

/**
 * @typedef {Object} ShowMoreRefs
 * @property {HTMLElement} showMoreButton - The button to toggle visibility of the items
 * @property {HTMLElement[]} showMoreItems - The hidden items to show and hide
 * @property {HTMLElement} showMoreContent - The content container to measure and animate
 */

/**
 * A custom element that manages the showing and hiding excess content items
 *
 * @extends {Component<ShowMoreRefs>}
 */

class ShowMoreComponent extends Component {
  // showMoreItems isn't required: a lazy filter list can start with none (see #loadLazyValues).
  requiredRefs = ['showMoreButton', 'showMoreContent'];

  /**
   * @type {boolean}
   */
  #expanded = false;

  /**
   * @type {boolean}
   */
  #disableOnDesktop = false;

  /**
   * @type {number}
   */
  #collapsedHeight = 0;

  /**
   * @type {'mobile:hidden' | 'hidden'}
   */
  #disabledClass = 'hidden';

  /**
   * @type {'MOBILE' | 'DESKTOP'}
   */
  get #currentBreakpoint() {
    return isMobileBreakpoint() ? 'MOBILE' : 'DESKTOP';
  }

  /**
   * @type {Animation | undefined}
   */
  #animation;

  /**
   * @constant {number}
   */
  #animationSpeed = 300;

  /**
   * @type {Promise<boolean> | undefined}
   */
  #lazyValuesRequest;

  /**
   * A filter change re-renders the list with only its visible values again (the morph also
   * drops `data-lazy-loaded`), so an expanded list reloads the rest for the new filters.
   */
  #lazyListObserver = new MutationObserver((mutations) => {
    if (this.dataset.expanded !== 'true') return;

    const reset = mutations.some(
      (mutation) =>
        mutation.target instanceof Element &&
        mutation.target.matches('ul[data-lazy-values]') &&
        !mutation.target.hasAttribute('data-lazy-loaded')
    );
    if (!reset) return;

    this.#loadLazyValues().then((loaded) => {
      if (!loaded || this.dataset.expanded !== 'true') return;
      this.refs.showMoreItems?.forEach((item) => item.classList.remove(this.#disabledClass));
    });
  });

  connectedCallback() {
    super.connectedCallback();
    this.#updateBreakpointState();
    this.addEventListener('pointerover', this.#prefetchLazyValues);
    this.addEventListener('focusin', this.#prefetchLazyValues);
    this.#lazyListObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['data-lazy-loaded'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#lazyListObserver.disconnect();
  }

  /**
   * The filter list whose "Show more" values haven't been loaded yet, if any.
   * @returns {HTMLUListElement | null}
   */
  get #pendingLazyList() {
    return this.querySelector('ul[data-lazy-values]:not([data-lazy-loaded])');
  }

  /**
   * Warms the values request when the shopper heads for "Show more".
   * @param {Event} event
   */
  #prefetchLazyValues = (event) => {
    if (!(event.target instanceof Element) || !this.refs.showMoreButton?.contains(event.target)) return;
    if (!this.#pendingLazyList) return;
    loadFacetValues().catch(() => {});
  };

  /**
   * Inserts the list's "Show more" values, fetched on demand. Resolves false if they
   * couldn't be loaded (the list stays collapsed and the next click retries).
   * @returns {Promise<boolean>}
   */
  #loadLazyValues() {
    const list = this.#pendingLazyList;
    if (!list) return Promise.resolve(true);
    if (this.#lazyValuesRequest) return this.#lazyValuesRequest;

    const { showMoreButton } = this.refs;
    showMoreButton.setAttribute('aria-busy', 'true');
    list.removeAttribute('data-lazy-error');

    this.#lazyValuesRequest = loadFacetValues()
      .then((valuesDocument) => {
        this.#insertLazyValues(list, valuesDocument);
        return true;
      })
      .catch((error) => {
        console.error(error);
        list.setAttribute('data-lazy-error', '');
        return false;
      })
      .finally(() => {
        showMoreButton.removeAttribute('aria-busy');
        this.#lazyValuesRequest = undefined;
      });

    return this.#lazyValuesRequest;
  }

  /**
   * @param {HTMLUListElement} list
   * @param {Document} valuesDocument
   */
  #insertLazyValues(list, valuesDocument) {
    const param = list.dataset.facetParam;
    const idSuffix = list.dataset.facetIdSuffix ?? '';
    const source = [...valuesDocument.querySelectorAll('ul[data-facet-values]')].find(
      (element) => element.getAttribute('data-facet-values') === param
    );

    if (source) {
      const fragment = document.createDocumentFragment();

      for (const item of source.children) {
        const input = item.querySelector('input');
        // Active values are already in the page - keep that element (and its state), in order.
        const existing = input
          ? [...list.querySelectorAll('input')].find((el) => el.name === input.name && el.value === input.value)
          : null;

        if (existing) {
          const existingItem = existing.closest('li');
          if (existingItem && existingItem.parentElement === list) fragment.append(existingItem);
          continue;
        }

        const node = /** @type {Element} */ (document.importNode(item, true));
        for (const element of [node, ...node.querySelectorAll('[id], [for]')]) {
          for (const attribute of ['id', 'for']) {
            const value = element.getAttribute(attribute);
            if (value?.includes(FACET_ID_SUFFIX_TOKEN)) {
              element.setAttribute(attribute, value.replace(FACET_ID_SUFFIX_TOKEN, idSuffix));
            }
          }
        }
        fragment.append(node);
      }

      list.append(fragment);
    }

    list.setAttribute('data-lazy-loaded', '');
    // Pick up the new showMoreItems now rather than on the next mutation callback.
    super.updatedCallback();
  }

  /**
   * Updates the current breakpoint and apprpropriate disabled class
   */
  #updateBreakpointState = () => {
    this.#disableOnDesktop = this.dataset.disableOnDesktop === 'true';
    this.#disabledClass = this.#disableOnDesktop ? 'mobile:hidden' : 'hidden';
  };

  /**
   * Handles expanding the content
   * @returns {{startHeight: number, endHeight: number}}
   */
  #expand = () => {
    const { showMoreItems, showMoreContent } = this.refs;

    this.#collapsedHeight = showMoreContent.offsetHeight;
    const startHeight = this.#collapsedHeight;

    showMoreItems?.forEach((item) => item.classList.remove(this.#disabledClass));

    return {
      startHeight,
      endHeight: showMoreContent.scrollHeight,
    };
  };

  /**
   * Handles collapsing the content
   * @returns {{startHeight: number, endHeight: number}}
   */
  #collapse = () => {
    const { showMoreContent } = this.refs;
    const startHeight = showMoreContent.offsetHeight;
    const endHeight = this.#collapsedHeight;

    return { startHeight, endHeight };
  };

  /**
   * Initializes a height transition
   * @param {number} startHeight
   * @param {number} endHeight
   */
  #animateHeight = (startHeight, endHeight) => {
    const { showMoreContent } = this.refs;

    showMoreContent.style.overflow = 'hidden';
    this.#animation?.cancel();

    this.#animation = showMoreContent.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
      },
      {
        duration: this.#animationSpeed,
        easing: 'ease-in-out',
      }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish();
  };

  /**
   * Handles the animation finish event.
   */
  #onAnimationFinish() {
    const { showMoreContent, showMoreItems } = this.refs;

    if (this.#expanded) {
      showMoreItems?.forEach((item) => item.classList.add(this.#disabledClass));
    }

    showMoreContent.style.removeProperty('height');
    showMoreContent.style.overflow = '';
    this.#expanded = !this.#expanded;
  }

  /**
   * Toggles the expansion state of the content.
   *
   * @param {Event} event - The click event
   */
  toggle = async (event) => {
    event.preventDefault();

    this.#updateBreakpointState();

    if (this.#currentBreakpoint === 'DESKTOP' && this.#disableOnDesktop) return;

    if (!this.#expanded && this.#pendingLazyList) {
      // A click while the values are loading is ignored (the button is also aria-busy).
      if (this.#lazyValuesRequest) return;
      if (!(await this.#loadLazyValues())) return;
    }

    const { startHeight, endHeight } = !this.#expanded ? this.#expand() : this.#collapse();

    this.dataset.expanded = this.#expanded ? 'false' : 'true';
    this.refs.showMoreButton.setAttribute('aria-expanded', this.dataset.expanded);

    this.#animateHeight(startHeight, endHeight);
  };
}

if (!customElements.get('show-more-component')) {
  customElements.define('show-more-component', ShowMoreComponent);
}
